import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, EMPTY, forkJoin, map, Subject, switchMap, tap } from 'rxjs';

import { EpdEditSectionComponent } from './components/epd-edit-section/epd-edit-section.component';
import { EpdExportModalComponent } from './components/epd-export-modal/epd-export-modal.component';
import { EpdInfoTableComponent } from './components/epd-info-table/epd-info-table.component';
import { EpdPepPreviewComponent } from './components/epd-pep-preview/epd-pep-preview.component';

import { CookiesService } from '@services/cookies.service';
import { MakersiteService } from '@services/makersite.service';
import { ToastrService } from '@services/toaster.service';
import { ToolbarEventService } from '@services/toolbar-event.service';
import { EpdV2Service } from './services/epd-v2.service';

import { ButtonComponent, CardComponent, ChipComponent, DropdownMenuComponent, LoaderModule } from '@mds/ui';

import Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgxMasonryComponent, NgxMasonryModule } from 'ngx-masonry';

import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { MdsIcon } from '@mds/icons';
import { EpdCarbonFootprintComponent } from '@pages/dashboards/epd-v2/components/epd-carbon-footprint/epd-carbon-footprint.component';
import { EpdChgIngredientsComponent } from '@pages/dashboards/epd-v2/components/epd-chg-ingredients/epd-chg-ingredients.component';
import { EpdImpactTableComponent } from '@pages/dashboards/epd-v2/components/epd-impact-table/epd-impact-table.component';
import { EPD_STANDARDS, Epdresponse, Export, PCR, seENVs, Standard } from '@pages/dashboards/epd-v2/utils/epd';
import { AnalyzeLayersService } from '@services/analyze.layers.service';
import { AnalyzeService } from '@services/analyze.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { MenuService } from '@services/menu.service';
import { ReusableService } from '@services/reusable.service';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { sortBy } from '@utils/sort-by';
import { EPD_CLIMATE_CHART_OPTIONS, EPD_MATERIAL_CHART_OPTIONS } from './utils/chart-options';
import { largestRemainderRound } from './utils/largest-remainder-round';

enum EPDMenuActions {
  SHARE = 'share',
  INVITE = 'invite',
  EDIT = 'edit',
  EXPORT_PEP = 'pep_export',
  EXPORT_PIM = 'pim_import',
  EXPORT_EXCEL = 'excel_export',
  EXPORT_PDF = 'pdf_export',
  EXPORT_WORD = 'word_export',
  EXPORT_XML = 'xml_export',
  SAVE = 'save'
}

@Component({
  selector: 'app-epd-v2',
  standalone: true,
  imports: [
    CommonModule,
    EpdEditSectionComponent,
    EpdInfoTableComponent,
    ButtonComponent,
    ChipComponent,
    DropdownMenuComponent,
    NgxMasonryModule,
    LoaderModule,
    HighchartsChartModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    CardComponent,
    EpdImpactTableComponent,
    EpdCarbonFootprintComponent,
    EpdChgIngredientsComponent
  ],
  providers: [EpdV2Service],
  templateUrl: './epd-v2.component.html',
  styleUrls: ['./epd-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpdV2Component implements OnInit, OnDestroy {
  @ViewChild(NgxMasonryComponent) masonry: NgxMasonryComponent;
  updateData$ = new Subject<void>();
  fetchData$ = new Subject<void>();
  fetchLanguage$ = new Subject<void>();
  showPreview = false;
  canPreview: Export | null = null;
  Highcharts = Highcharts;
  rid: string;
  isLoading = true;
  isSEColorThemeEnabled = false;
  tableData: any = [];
  section: any = [];
  chartsData: any = [];
  productName: string;
  selectedPcr: PCR;
  data;
  payload = null;
  protected router = inject(Router);
  protected dialog = inject(MatDialog);
  protected destroyRef = inject(DestroyRef);
  protected cdr = inject(ChangeDetectorRef);
  protected epdV2Service = inject(EpdV2Service);
  protected toastrService = inject(ToastrService);
  protected cookiesService = inject(CookiesService);
  protected activatedRoute = inject(ActivatedRoute);
  protected reusableService = inject(ReusableService);
  protected makersiteService = inject(MakersiteService);
  protected toolbarEventService = inject(ToolbarEventService);
  protected menuService = inject(MenuService);
  protected analyzeService = inject(AnalyzeService);
  protected globalDataService = inject(GlobalDataService);
  protected analyzeLayersService = inject(AnalyzeLayersService);
  protected eventDelegatorService = inject(EventDelegatorService);
  protected pcrList: any = [];
  protected methodList: Standard[];
  protected languages: any[] | null;
  protected selectedLanguage;
  protected selectedMethod: Standard;
  protected ngxEventBus = [];

  protected selectedLayer: any;
  protected layers: any[];
  protected materialsBreakdown: any;
  protected showAllLCA = false;
  protected readonly MdsIcon = MdsIcon;

  get previewButtonLabel() {
    return this.payload ? 'Save & Export' : 'Preview & Export';
  }

  get isNewEPD() {
    return ![EPD_STANDARDS.EF_30].includes(this.selectedMethod.value as EPD_STANDARDS);
  }

  ngOnInit(): void {
    // Enable SE color theme for charts for SE Sandboxes
    const isSEThemeEnabled = seENVs.includes(window.location.hostname);
    if (isSEThemeEnabled) this.cookiesService.setCookie('epd_se_theme_enabled', true);

    if (this.cookiesService.getCookie('epd_se_theme_enabled') === 'true') {
      this.isSEColorThemeEnabled = true;
    }

    combineLatest([
      this.activatedRoute.paramMap,
      this.activatedRoute.queryParamMap,
      this.epdV2Service.fetchStandardsList()
    ])
      .pipe(
        map(([params, queryParams, methods]) => ({
          rid: params.get('rid'),
          method: queryParams.get('method'),
          methods
        })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ rid, method, methods }) => {
        this.rid = rid;
        this.methodList = methods;

        // set default
        if (!method) {
          method = EPD_STANDARDS.EN_15804_A2;
        }
        this.selectedMethod = methods.find((standard: Standard) => standard.value === method);
        this.initToolbar();
        if (!this.selectedMethod) {
          this.toastrService.showMessage('No standard found');
          this.router.navigate([`/dashboard`]);
        } else {
          this.fetchData$.next();
        }
      });

    this.updateData$
      .pipe(
        switchMap(() => this.epdV2Service.submitData(encodeURI(this.rid.replace('#', '')), this.payload)),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe((res) => {
        this.payload = null;
        this.menuService.updateMenuItem('save', { visible: false });
        this.toolbarEventService.updateMegaMenuActions.next({ save: false });
        this.fetchData$.next();
      });

    this.fetchData$
      .pipe(
        tap(() => {
          this.isLoading = true;
        }),
        switchMap(() =>
          this.isNewEPD
            ? this.epdV2Service.fetchData(this.rid, this.selectedPcr?.rid, this.selectedMethod?.value)
            : this.epdV2Service.fetchV1Data(this.rid, this.selectedMethod?.value)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: Epdresponse) => {
          if (this.isNewEPD) {
            this.processNewEPD(res);
            if (this.showPreview) {
              this.previewAndExport();
            }
          } else {
            this.processEPD(res);
          }
          this.updateMenu();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.fetchLanguage$.next();
        },
        error: () => {
          this.router.navigate(['/dashboard']);
        }
      });

    this.fetchLanguage$
      .pipe(
        switchMap(() => {
          this.languages = null;
          return this.epdV2Service.fetchLanguagesList(this.selectedMethod?.value);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((languages) => {
        this.languages = languages;
        if (languages?.length > 1) this.selectedLanguage = languages.find((lang) => lang.key === 'en');
      });

    this.subscribeToMenuEvent();
  }

  getMaterialsBreakdownChart() {
    if (!this.selectedLayer.rid) {
      this.toastrService.showMessage('Rid is missing in Impact Category.', 2000);
      this.materialsBreakdown = { rid: this.rid.replace('#', '') };
    } else {
      this.materialsBreakdown = { rid: this.rid.replace('#', ''), layerRid: this.selectedLayer.rid.replace('#', '') };
    }
  }

  getMenuActions(event: { action: { key: EPDMenuActions } }) {
    const { key } = event.action;
    switch (key) {
      case EPDMenuActions.SHARE:
        this.showShare();
        break;
      case EPDMenuActions.INVITE:
        this.invite();
        break;
      case EPDMenuActions.EDIT:
        this.openComposer();
        break;
      case EPDMenuActions.EXPORT_WORD:
        this.exportEPD(key);
        break;
      case EPDMenuActions.EXPORT_PEP:
      case EPDMenuActions.EXPORT_PIM:
      case EPDMenuActions.EXPORT_EXCEL:
      case EPDMenuActions.EXPORT_PDF:
      case EPDMenuActions.EXPORT_XML:
        this.additionalExports(key);
        break;
      case EPDMenuActions.SAVE:
        this.updateData$.next();
        break;
      default:
        break;
    }
  }

  resetToolbar() {
    const allActionsValidation = {
      show: ['share', 'invite', 'compose'],
      hidden: ['save'],
      disabled: []
    };

    const allActions = {
      action: [
        { key: 'share', value: 'Share' },
        { key: 'invite', value: 'Invite' },
        { key: 'compose', value: 'Compose' }
      ],
      additionalActivity: [
        { key: 'export_pdf', value: 'Export PDF' },
        { key: 'export_xml', value: 'Export XML' },
        { key: 'export_docx', value: 'Export Word' },
        { key: 'export_excel', value: 'Export Excel' },
        { key: 'export_pim_import', value: 'PIM Import' },
        { key: 'export_pep_export', value: 'PEP Export' }
      ],
      commonActivity: [{ key: 'save', value: 'Save' }],
      validations: allActionsValidation
    };
    this.globalDataService.setAllActions(allActions);
  }

  getMegamenuEvents(event) {
    const { key } = event.action;
    switch (key) {
      case 'share':
        this.showShare();
        break;
      case 'invite':
        this.invite();
        break;
      case 'compose':
        this.openComposer();
        break;
      case 'export_pdf':
        this.additionalExports(EPDMenuActions.EXPORT_PDF);
        break;
      case 'export_xml':
        this.additionalExports(EPDMenuActions.EXPORT_XML);
        break;
      case 'export_excel':
        this.additionalExports(EPDMenuActions.EXPORT_EXCEL);
        break;
      case 'export_pim_import':
        this.additionalExports(EPDMenuActions.EXPORT_PIM);
        break;
      case 'export_pep_export':
        this.additionalExports(EPDMenuActions.EXPORT_PEP);
        break;
      case 'export_docx':
        this.exportEPD(EPDMenuActions.EXPORT_WORD);
        break;
      case 'save':
        this.updateData$.next();
        break;
      default:
        break;
    }
  }

  onEditSectionUpdated(event) {
    // for edit section models
    const section = this.data.sections.find((s: any) => s.key === event.key);
    if (!section.value) {
      section.value = event.value;
      return;
    }

    const updated = {};
    Object.entries(section.value).forEach(([k, v]) => {
      if (v !== event.value[k]) {
        updated[k] = event.value[k];
      }
    });

    if (Object.keys(updated).length) {
      this.payload = updated;
      this.menuService.updateMenuItem('save', { visible: true });
      this.toolbarEventService.updateMegaMenuActions.next({ save: true });
    }
  }

  onChangeMethodology(methods: Standard[]) {
    [this.selectedMethod] = methods;
    this.fetchData$.next();
  }

  onChangePcr(selected: PCR[]) {
    [this.selectedPcr] = selected;
    this.fetchData$.next();
  }

  onChangeLanguage(language: any[]) {
    [this.selectedLanguage] = language;
    this.cdr.markForCheck();
  }

  reloadMasonryLayout() {
    this.masonry?.reloadItems();
    this.masonry?.layout();
  }

  initChart(chartsData) {
    const TOP_N = 7;
    const rawItems = chartsData[0].items.map((row) => ({
      name: (row.category || row.top_level_category) as string,
      pct: (row.percentage as number) * 100
    }));
    rawItems.sort((a, b) => b.pct - a.pct);

    // Separate named categories from any backend "Others" items
    const named = rawItems.filter((r) => r.name !== 'Others');
    const othersPct = rawItems.filter((r) => r.name === 'Others').reduce((sum, r) => sum + r.pct, 0);
    const shown = named.slice(0, TOP_N);
    const overflowPct = named.slice(TOP_N).reduce((sum, r) => sum + r.pct, 0) + othersPct;
    if (overflowPct > 0) {
      shown.push({ name: 'Others', pct: overflowPct });
    }

    const roundedPcts = largestRemainderRound(shown.map((r) => r.pct));
    const chart1Data = shown.map((row, i) => ({
      name: row.name,
      y: roundedPcts[i]
    }));

    const chartCategories = [];
    const chart2Data: any = [
      {
        colorByPoint: true,
        data: []
      }
    ];
    chartsData[1].items.forEach((row) => {
      chartCategories.push(row.category || row.top_level_category);
      chart2Data[0].data.push(+row.percentage.toFixed(2));
    });

    this.chartsData = [
      {
        title: chartsData[0].title,
        options: {
          ...EPD_MATERIAL_CHART_OPTIONS,
          colors: this.isSEColorThemeEnabled
            ? [
                '#6fa4a7',
                '#4ea084',
                '#259b57',
                '#009231',
                '#008735',
                '#007c39',
                '#00713d',
                '#006740',
                '#005c44',
                '#005148',
                '#00474b',
                '#003c4f'
              ]
            : EPD_MATERIAL_CHART_OPTIONS.colors,
          series: [
            {
              type: 'pie',
              data: chart1Data
            }
          ]
        }
      },
      {
        title: chartsData[1].title,
        options: {
          ...EPD_CLIMATE_CHART_OPTIONS,
          colors: this.isSEColorThemeEnabled
            ? ['#6fa4a7', '#1b994d', '#008337', '#006b3f', '#005447', '#003c4f']
            : EPD_CLIMATE_CHART_OPTIONS.colors,
          xAxis: {
            categories: chartCategories
          },
          series: chart2Data
        }
      }
    ];
  }

  downloadAttachment(url) {
    this.epdV2Service.downloadAdditionalExports(url).subscribe(
      (response) => {
        const blob = response.body;
        const contentDisposition = response.headers.get('content-disposition');
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');

        anchor.href = url;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
          if (match && match[1]) {
            anchor.download = decodeURIComponent(match[1].replace(/['"]/g, '').trim());
          }
        } else anchor.setAttribute('download', '');
        anchor.click();
        setTimeout(() => {
          anchor.remove();
          URL.revokeObjectURL(url);
        }, 100);
      },
      (error) => {
        this.toastrService.show('Download failed', 2000);
      }
    );
  }

  previewAndExport() {
    if (this.payload) {
      this.showPreview = true;
      this.updateData$.next();
      return;
    }

    let { url } = this.canPreview;
    if (!url) return;

    if (this.selectedLanguage) {
      url += `&language=${this.selectedLanguage.key}`;
    }

    this.data.charts = this.chartsData;
    this.isLoading = true;

    this.dialog
      .open(EpdPepPreviewComponent, {
        data: { url },
        panelClass: 'standard-dialog'
      })
      .afterClosed()
      .subscribe((res) => {
        this.cdr.markForCheck();
        this.isLoading = false;
      });
  }

  private initToolbar() {
    this.makersiteService
      .getTileCards(this.rid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((products: any) => {
        this.setMenu(products[0]);
      });

    // TODO after remove of old ui keep only setMenu() function.
    this.resetToolbar();
    this.globalDataService.setActiveApp(undefined);
    const layers = this.makersiteService.getMattermapLayers();
    const product = this.makersiteService.getTileCards(this.rid);
    forkJoin([layers, product]).subscribe(([layers, products]: [any, any]) => {
      const layerList = layers.layers;
      const app = layerList
        .find((e) => e.category === 'Business')
        .list.find((e) => e.layer === 'Epd-dashboard' || e.module === 'epd-dashboard');
      this.globalDataService.setMegaMenuList(layerList);
      this.globalDataService.setSelectedApp(app);
      this.globalDataService.setActiveApp(app);
    });

    this.globalDataService.setUrlParam({ rid: this.rid });
    this.globalDataService.setCurrentActiveApp({ name: 'app.epd-v2' });
    this.analyzeService.setAnalyzeRid(this.rid);

    this.ngxEventBus.push(
      this.eventDelegatorService.on('ngx-event-epd-v2', (event) => {
        if (event.type === 'mega-menu-event' && event.action === 'switch-menu') {
          this.analyzeLayersService.switchApp(event);
        } else if (event.type === 'toolbar-reload-event') {
          this.fetchData$.next();
        } else if (event.type === 'mega-action-event') {
          this.getMegamenuEvents(event);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.ngxEventBus.forEach((sub) => {
      sub?.unsubscribe();
    });
  }

  private processNewEPD(res: Epdresponse) {
    this.pcrList = res.available_pcrs.map((pcr) => ({
      ...pcr,
      text: pcr.name,
      value: pcr.rid,
      updated: new Date()
    }));
    this.section = res.data.sections;
    this.initChart(res.data.charts);
    this.data = res.data;
    this.productName = res.data.metadata.name;
    this.selectedPcr = res.data.pcr;
    this.tableData = Object.entries(res.data.tables).map(([a, v]) => ({ ...v, printLayout: 'autoHeight' }));
    this.canPreview = (res.data.available_exports || []).find((exp) => exp.key === EPDMenuActions.EXPORT_PDF);
    const available_exports: string[] = (res.data.available_exports || []).map((exp) => exp.key);
    const isPimExportEnabled = available_exports.includes('pim_import');
    const isPepExportEnabled = available_exports.includes('pep_export');
    const isExcelExportEnabled = available_exports.includes('excel_export');
    const isXMLExportEnabled = available_exports.includes('xml_export');
    this.toolbarEventService.updateMegaMenuActions.next({ reset: true });
    this.toolbarEventService.updateMegaMenuActions.next({
      share: true,
      invite: true,
      compose: true,
      export_pep_export: isPepExportEnabled,
      export_pim_import: isPimExportEnabled,
      export_excel: isExcelExportEnabled,
      export_xml: isXMLExportEnabled,
      export_pdf: this.canPreview
    });
    this.cdr.markForCheck();
  }

  private processEPD(res: any) {
    sortBy(res.epds[0].layers, 'sortlevel');
    this.selectedPcr = res.epds[0].pcr;
    this.pcrList = res.epds.map(({ pcr }) => ({ ...pcr, text: pcr.name, value: pcr.rid }));
    this.data = res;
    this.canPreview = null;
    this.productName = res.name;
    this.layers = res.epds[0].layers;
    this.data.updated = res.epds[0].updated;
    this.selectedLayer = { ...this.layers[0] };
    this.getMaterialsBreakdownChart();
    this.toolbarEventService.updateMegaMenuActions.next({ reset: true });
    this.toolbarEventService.updateMegaMenuActions.next({
      share: true,
      invite: true,
      compose: true,
      export_pdf: true,
      export_docx: true
    });
    this.cdr.markForCheck();
  }

  private setMenu(entity: any) {
    this.menuService.setMenuConfig({
      activeApp: {
        mainIcon: {
          icon: MdsIcon.APP_EPD_DASHBOARD
        },
        name: 'EPD Dashboard',
        icons: [],
        subtitle: '',
        menuTrigger: 'selectApps'
      },
      activeEntity: {
        mainIcon: {
          icon: MdsIcon.PRODUCT_GROUP,
          pathColors: ['#fff']
        },
        rid: entity?.rid,
        name: entity?.title,
        icons: [{ key: getEntityTypeIcon(entity.stage) }],
        subtitle: entity.card_type_label
      },
      centerActions: [],
      rightActions: [
        {
          label: 'Edit',
          key: EPDMenuActions.EDIT,
          icon: MdsIcon.EDIT,
          selected: false,
          disabled: false,
          visible: true
        },
        {
          label: 'Save',
          key: EPDMenuActions.SAVE,
          icon: MdsIcon.SAVE,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'PEP Export',
          key: EPDMenuActions.EXPORT_PEP,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'PIM Import',
          key: EPDMenuActions.EXPORT_PIM,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export PDF',
          key: EPDMenuActions.EXPORT_PDF,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export Excel',
          key: EPDMenuActions.EXPORT_EXCEL,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export Word',
          key: EPDMenuActions.EXPORT_WORD,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Export XML',
          key: EPDMenuActions.EXPORT_XML,
          icon: MdsIcon.DOWNLOAD,
          selected: false,
          disabled: false,
          visible: false
        },
        {
          label: 'Share',
          key: EPDMenuActions.SHARE,
          icon: MdsIcon.SHARE,
          selected: false,
          disabled: false,
          visible: true
        },
        {
          label: 'Invite',
          key: EPDMenuActions.INVITE,
          icon: MdsIcon.INVITE,
          selected: false,
          disabled: false,
          visible: true
        }
      ]
    });
  }

  private updateMenu() {
    const unchangedActions = [EPDMenuActions.EDIT, EPDMenuActions.SHARE, EPDMenuActions.INVITE];
    const { rightActions } = this.menuService.menuConfigSubject.getValue();
    const availableExports = this.isNewEPD
      ? [...this.data.available_exports.map((availableExport: any) => availableExport.key), ...unchangedActions]
      : [EPDMenuActions.EXPORT_WORD, EPDMenuActions.EXPORT_PDF, ...unchangedActions];
    const updatedActions = (rightActions || []).map((action) => {
      return { [action.key]: { visible: availableExports.includes(action.key as EPDMenuActions) } };
    });
    this.menuService.updateMenuItems(updatedActions);
    this.cdr.markForCheck();
  }

  private subscribeToMenuEvent() {
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reload: boolean) => {
      if (reload) {
        this.fetchData$.next();
      }
    });
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.getMenuActions({ action: value });
      }
    });
  }

  private showShare() {
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    const link = document.location.pathname;
    const data = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid: this.rid,
        usersLackingAccessList: ''
      }
    };

    this.dialog.open(InviteShareComponent, {
      data,
      panelClass: 'show-invite'
    });
  }

  private invite() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite'
    });
  }

  private openComposer() {
    this.router.navigate([`/mattermaps/overview/${this.rid}/edit`]);
  }

  private additionalExports(action: EPDMenuActions) {
    if (action === EPDMenuActions.EXPORT_PDF && !this.isNewEPD) {
      this.exportEPD(EPDMenuActions.EXPORT_PDF);
      return;
    }
    // Display dialog when sections exists, else download file from endpoint
    const availableExports: any[] = this.data.available_exports || [];
    let data: any;
    if ([EPDMenuActions.EXPORT_PIM, EPDMenuActions.EXPORT_PEP].includes(action)) {
      const exportSection: Export = availableExports.find((ex) => ex.key === action);
      data = { ...exportSection, rid: this.rid };
    } else if ([EPDMenuActions.EXPORT_EXCEL, EPDMenuActions.EXPORT_PDF, EPDMenuActions.EXPORT_XML].includes(action)) {
      const availableExport: Export = availableExports.find((ex) => ex.key === action);
      this.downloadAttachment(availableExport.url);
      return;
    }
    this.dialog.open(EpdExportModalComponent, {
      data
    });
  }

  private exportEPD(exportType: EPDMenuActions) {
    // TODO
    // Basic exports to be fixed only if requested by Users
    this.toastrService.showMessage('Please wait, It will take few seconds to Export.', 3000);
    const filename = `Makersite_EPD Dashboard of ${this.data.name}`;
    if (exportType === EPDMenuActions.EXPORT_PDF) this.reusableService.export('epdDashboard', filename, 'pdf');
    if (exportType === EPDMenuActions.EXPORT_WORD) {
      this.reusableService.export('epdDashboard', filename, 'docx');
    }
  }
}
